/**
 * worksheets/$slug.tsx — Worksheet detail page (Task 22 + 2026-07-20 gating spec).
 *
 * Lives at `/worksheets/$slug`. Thin route wrapper that resolves the
 * slug from the catalog cache, hydrates `isEnabled` from catalog state,
 * checks session, and delegates to <WorksheetDetailSurface> for rendering.
 *
 * Gate logic:
 *   - WorksheetDetailSurface branches on isLoggedIn + isEnabled
 *   - Guest + ready → preview only (no PDF, no download)
 *   - Login + disabled → "Worksheet ini sedang tidak aktif"
 *   - Login + enabled → full PDF + download (existing)
 */

import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { StatePanel } from '../../components/ui/StatePanel.js';
import { WorksheetDetailSurface } from '../../components/public/WorksheetDetailSurface.js';
import {
  publicCatalogQuery,
  catalogStateQuery,
  sessionQuery,
  mergeCatalogWithState,
  findWorksheetBySlug,
} from '../../lib/api/queries.js';

export const Route = createFileRoute('/worksheets/$slug')({
  loader: async ({ context }) => {
    await context.queryClient.prefetchQuery(publicCatalogQuery);
    return null;
  },
  component: WorksheetDetailPage,
});

function WorksheetDetailPage(): ReactNode {
  const { slug } = Route.useParams();
  const catalog = useQuery(publicCatalogQuery);
  const state = useQuery(catalogStateQuery);
  const session = useQuery(sessionQuery);

  const stateMap = state.data?.ok ? (state.data.data?.worksheets ?? {}) : {};
  const worksheets = mergeCatalogWithState(
    catalog.data?.worksheets ?? [],
    stateMap,
    'worksheetId',
  );
  const worksheet = findWorksheetBySlug(worksheets, slug);

  if (catalog.isLoading) {
    return (
      <div className="page-shell">
        <p className="form-message">Memuat katalog…</p>
      </div>
    );
  }

  if (!worksheet) {
    return (
      <div className="page-shell">
        <PageHeader
          eyebrow="Worksheet"
          title="Worksheet tidak ditemukan"
          description={`Kode '${slug}' tidak cocok dengan worksheet apa pun di katalog.`}
        />
        <section className="section">
          <StatePanel
            variant="error"
            title="Slug tidak dikenal"
            description="Pilih worksheet dari katalog publik atau kembali ke dasbor kamu."
            action={
              <Link className="button button-primary" to="/worksheets">
                Buka katalog
              </Link>
            }
          />
        </section>
      </div>
    );
  }

  const isLoggedIn = !!(session.data?.ok && session.data.data?.user);

  return <WorksheetDetailSurface worksheet={worksheet} isLoggedIn={isLoggedIn} />;
}